import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Permission hierarchy: none < view < edit < delete
const PERMISSION_RANK = { none: 0, view: 1, edit: 2, delete: 3 };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Configure axios interceptor to attach token to all requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Global 401 handler — sign user out if their session was invalidated by an
  // admin permission change.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const invalidated = error?.response?.headers?.['x-session-invalidated'];
        if (error?.response?.status === 401 && invalidated) {
          toast.error('Your access was updated by an administrator. Please sign in again.');
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setPermissions(null);
          delete axios.defaults.headers.common['Authorization'];
          if (window.location.pathname !== '/login') {
            window.location.replace('/login');
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      if (response.data?.role) localStorage.setItem('userRole', response.data.role);
      // Fetch permissions after getting user
      await fetchPermissions();
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      // Only force logout on a real 401 (token rejected). On 404 / network /
      // CORS errors the token is still valid — we don't want to evict the
      // user just because an env-var or deploy is mid-flight. The interceptor
      // above already handles admin-invalidated 401s with the special header.
      if (error?.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/permissions/me`);
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // Set empty permissions on error
      setPermissions({ permissions: {}, module_labels: {} });
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      if (userData?.role) localStorage.setItem('userRole', userData.role);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Fetch permissions after login
      try {
        const permRes = await axios.get(`${API}/permissions/me`);
        setPermissions(permRes.data);
      } catch (permError) {
        console.error('Failed to fetch permissions:', permError);
        setPermissions({ permissions: {}, module_labels: {} });
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      // After registration, log the user in
      return await login(userData.email, userData.password);
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    setToken(null);
    setUser(null);
    setPermissions(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const hasRole = (allowedRoles) => {
    if (!user) return false;
    return allowedRoles.includes(user.role);
  };

  // Check if user has access to a specific module
  const hasModuleAccess = (module, level = 'view') => {
    if (!user) return false;
    // Admins have full access
    if (user.role === 'Admin') return true;

    if (!permissions?.permissions) return false;

    const userLevel = permissions.permissions[module] || 'none';
    const userRank = PERMISSION_RANK[userLevel] ?? 0;
    const requiredRank = PERMISSION_RANK[level] ?? 0;
    return userRank >= requiredRank;
  };

  // Get all accessible modules for the user
  const getAccessibleModules = () => {
    if (!user) return [];
    if (user.role === 'Admin') {
      // Admin can access all modules
      return permissions?.module_labels ? Object.keys(permissions.module_labels) : [];
    }
    if (!permissions?.permissions) return [];
    
    return Object.entries(permissions.permissions)
      .filter(([_, level]) => level !== 'none')
      .map(([module]) => module);
  };

  const value = {
    user,
    permissions,
    loading,
    login,
    register,
    logout,
    hasRole,
    hasModuleAccess,
    getAccessibleModules,
    refreshPermissions: fetchPermissions,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
